import { createContext, useContext, useState, type ReactNode } from 'react';
import type { Participant, TrackPublication } from 'livekit-client';

// =============================================================================
// voiceContext — the lightweight half of the voice surface.
//
// This module deliberately carries NO runtime LiveKit dependency (the only
// livekit-client imports here are `import type`, which are erased at build
// time). It holds the VoiceApi shape, the React context, and the useVoice()
// hook so that the many UI consumers (AppShell, VoiceChannelView, UserPopover,
// Modals, the sidebar bar, …) can read voice state without pulling the heavy
// `@livekit/components-react` + `livekit-client` bundle into the main chunk.
//
// The actual provider that imports LiveKit and connects to a room lives in
// `VoiceConnection.tsx`, which is lazy-loaded only once the user is (or was
// just) in a voice call. See `App.tsx` for the gating.
//
// Why a separate <VoiceStateProvider> instead of having VoiceConnection wrap
// the app (as it used to): the connected VoiceConnection computes `apiValue`
// from LiveKit room hooks and that value churns on every speaking flip. By
// holding the api in this small provider's `useState` and rendering the app as
// a *stable* `children` prop, provider re-renders (on api change) re-render
// only `useVoice()` consumers — never the whole app — exactly matching the old
// React-context behaviour. The heavy VoiceConnection mounts as a *sibling* and
// pushes its api up through `useVoicePublish()`. This keeps the app tree
// structurally stable (no remount when joining/leaving a call) AND keeps
// LiveKit out of the initial bundle.
// =============================================================================

/** Mirror of livekit-client's ConnectionQuality enum values (kept as string
 *  literals so this module stays free of runtime livekit imports). */
export type VoiceConnectionQuality = 'excellent' | 'good' | 'poor' | 'lost' | 'unknown';

export interface VoiceApi {
  inRoom: boolean;
  participants: Participant[];
  localParticipantIdentity: string | null;
  isSpeaking: (identity: string) => boolean;
  /** Whether a remote participant has their microphone muted (publication
   *  exists but is muted, or no publication at all). For our own identity
   *  this returns the local muted state. */
  isMuted: (identity: string) => boolean;
  /** Whether a remote participant has deafened themselves. Surfaced via
   *  LiveKit participant attributes (deafened: '1' or '0'). For our own
   *  identity this returns the local deafened state. */
  isDeafened: (identity: string) => boolean;
  /** Network connection quality for a participant (local included), as
   *  reported by the LiveKit server via ConnectionQualityChanged. 'unknown'
   *  until the first report arrives. UI shows an indicator for poor/lost. */
  getConnectionQuality: (identity: string) => VoiceConnectionQuality;
  muted: boolean;
  /** A moderator has server-muted us: LiveKit has revoked our microphone
   *  source, so unmuting is impossible until they lift it. */
  serverMuted: boolean;
  /** Get the volume for a remote participant. Returns 1.0 if no override
   *  has been set. Range: 0..2 (clamp on input).
   *  Always 1.0 for the local participant — your own track isn't played
   *  back to you. */
  getVolume: (identity: string) => number;
  /** Set the volume for a remote participant (persisted). Pass `null` to
   *  clear back to default. */
  setVolume: (identity: string, volume: number | null) => void;
  /** Get the volume override for a participant's screen-share audio.
   *  Independent of mic volume since the sharer might want to balance their
   *  voice differently from the audio of what they're sharing. */
  getShareVolume: (identity: string) => number;
  /** Set the volume for a participant's screen-share audio (persisted).
   *  Pass `null` to clear back to default. */
  setShareVolume: (identity: string, volume: number | null) => void;
  toggleMute: () => Promise<void>;
  deafened: boolean;
  toggleDeafen: () => Promise<void>;
  leave: () => void;
  // Subscribe to live mic level (in dB) + gate state for UI meters.
  // Returns an unsubscribe function. Returns null if no noise gate is active.
  onMicLevel: ((cb: (db: number, gateOpen: boolean) => void) => (() => void)) | null;
  // Screen sharing
  isSharingScreen: boolean;
  toggleScreenShare: () => Promise<void>;
  /** Swap the source of an in-progress screen share (re-prompts the picker)
   *  without dropping the publication — viewers, popout, and the watcher list
   *  all survive the switch. No-op when not currently sharing. */
  switchScreenShareSource: () => Promise<void>;
  // List of all screen-share track publications across all participants. UI
  // can pick one to render large; identity matches the participant's identity.
  screenShares: Array<{
    participantIdentity: string;
    publication: TrackPublication;
    /** Companion audio publication (system/tab audio shared alongside the
     *  screen video). Undefined when the sharer didn't enable audio capture. */
    audioPublication?: TrackPublication;
    /** Whether *we* are currently subscribed to this track (i.e. data flowing). */
    isSubscribed: boolean;
  }>;
  /** Subscribe or unsubscribe from a remote screen-share publication. */
  setShareSubscribed: (participantIdentity: string, subscribed: boolean) => void;
  /** Identities of remote participants currently watching (subscribed to) the
   *  given sharer's screen-share. Surfaced via LiveKit participant attributes
   *  (each viewer broadcasts the set of shares it's watching). Does NOT include
   *  the local user — the caller knows its own watching state. */
  getWatchers: (sharerIdentity: string) => string[];
  // Webcam
  /** Whether the local user's camera is currently published. */
  isCameraOn: boolean;
  /** Turn the local camera on/off (publishes/unpublishes a Camera track). */
  toggleCamera: () => Promise<void>;
  /** Flip the local camera between front/back (mobile). Swaps the source on
   *  the existing publication in place — remote viewers don't drop. */
  flipCamera: () => Promise<void>;
  /** Camera publication for a participant (local or remote), if published.
   *  Tiles attach its videoTrack to render the live camera feed. */
  getCameraPublication: (identity: string) => TrackPublication | undefined;
  /** Subscribe to LiveKit data-channel messages for the current room; returns
   *  an unsubscribe. Surfaced here because consumers like the meeting chat
   *  panel render OUTSIDE <LiveKitRoom> (VoiceConnection is a sibling driver,
   *  not a wrapper), so LiveKit's own useRoomContext/useDataChannel hooks are
   *  unavailable to them. Same reason the rest of this api exists. */
  subscribeData: (cb: (payload: Uint8Array) => void) => () => void;
  /** ID of share currently shown in the floating pop-out (null = none). */
  popoutShareIdentity: string | null;
  /** Pop a remote share out into a floating window (or close it). */
  setPopoutShare: (participantIdentity: string | null) => void;
}

export const NULL_API: VoiceApi = {
  subscribeData: () => () => {},
  inRoom: false,
  participants: [],
  localParticipantIdentity: null,
  isSpeaking: () => false,
  isMuted: () => false,
  serverMuted: false,
  isDeafened: () => false,
  getConnectionQuality: () => 'unknown',
  getVolume: () => 1,
  setVolume: () => {},
  getShareVolume: () => 1,
  setShareVolume: () => {},
  muted: false,
  toggleMute: async () => {},
  deafened: false,
  toggleDeafen: async () => {},
  leave: () => {},
  onMicLevel: null,
  isSharingScreen: false,
  toggleScreenShare: async () => {},
  switchScreenShareSource: async () => {},
  screenShares: [],
  setShareSubscribed: () => {},
  getWatchers: () => [],
  isCameraOn: false,
  toggleCamera: async () => {},
  flipCamera: async () => {},
  getCameraPublication: () => undefined,
  popoutShareIdentity: null,
  setPopoutShare: () => {},
};

const VoiceContext = createContext<VoiceApi>(NULL_API);

// Stable setter the (lazy, deep) VoiceConnection driver uses to publish its
// computed api up to VoiceStateProvider. Default no-op so reads outside a
// provider are harmless.
const VoicePublishContext = createContext<(api: VoiceApi) => void>(() => {});

export function useVoice(): VoiceApi {
  return useContext(VoiceContext);
}

/** Used by the VoiceConnection driver to publish its api. The returned setter
 *  is referentially stable (it's a useState setter), so effects depending on it
 *  don't re-run spuriously. */
export function useVoicePublish(): (api: VoiceApi) => void {
  return useContext(VoicePublishContext);
}

/**
 * Always-mounted, lightweight provider. Holds the live VoiceApi in state and
 * renders the app as a stable `children` prop. The heavy VoiceConnection driver
 * (rendered within `children` by App, lazily) calls the publish setter to push
 * its api in. Because `children` is a stable prop, api updates re-render only
 * `useVoice()` consumers, not the whole app.
 */
export function VoiceStateProvider({ children }: { children: ReactNode }) {
  const [api, setApi] = useState<VoiceApi>(NULL_API);
  return (
    <VoicePublishContext.Provider value={setApi}>
      <VoiceContext.Provider value={api}>{children}</VoiceContext.Provider>
    </VoicePublishContext.Provider>
  );
}
